import type { CanvasDocumentState } from './documentModel';
import { cloneDocument, getElementLayout } from './documentModel';
import type { Point } from './viewport';

export type MoveElementsOperation = {
  type: 'moveElements';
  elementIds: string[];
  before: Record<string, Point>;
  after: Record<string, Point>;
};

export type CanvasOperation = MoveElementsOperation;

export function applyOperation(
  document: CanvasDocumentState,
  operation: CanvasOperation,
): CanvasDocumentState {
  const next = cloneDocument(document);

  switch (operation.type) {
    case 'moveElements':
      for (const elementId of operation.elementIds) {
        const layout = getElementLayout(next, elementId);
        const target = operation.after[elementId];
        if (layout && target) {
          layout.x = target.x;
          layout.y = target.y;
        }
      }
      return next;
    default:
      return next;
  }
}

export function invertOperation(operation: CanvasOperation): CanvasOperation {
  switch (operation.type) {
    case 'moveElements':
      return { ...operation, before: operation.after, after: operation.before };
    default:
      return operation;
  }
}

export class CommandRegistry {
  private undoStack: CanvasOperation[] = [];
  private redoStack: CanvasOperation[] = [];

  get undoDepth(): number {
    return this.undoStack.length;
  }

  get redoDepth(): number {
    return this.redoStack.length;
  }

  commit(document: CanvasDocumentState, operation: CanvasOperation): CanvasDocumentState {
    const next = applyOperation(document, operation);
    this.undoStack.push(operation);
    this.redoStack = [];
    return next;
  }

  undo(document: CanvasDocumentState): CanvasDocumentState | null {
    const operation = this.undoStack.pop();
    if (!operation) {
      return null;
    }
    const inverse = invertOperation(operation);
    const next = applyOperation(document, inverse);
    this.redoStack.push(operation);
    return next;
  }

  redo(document: CanvasDocumentState): CanvasDocumentState | null {
    const operation = this.redoStack.pop();
    if (!operation) {
      return null;
    }
    const next = applyOperation(document, operation);
    this.undoStack.push(operation);
    return next;
  }
}

export function createMoveOperation(
  elementIds: string[],
  before: Record<string, Point>,
  after: Record<string, Point>,
): MoveElementsOperation {
  return { type: 'moveElements', elementIds, before, after };
}
